import React from 'react';
import {
    Box,
    Divider,
    Stack,
    Tooltip,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
    Alert,
    Snackbar
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../auth/AuthContext';
import useTokenCountdown from '../../hooks/useTokenCountdown';
import { getSubscriptionPlans, changeSubscriptionPlan, getCurrentUser } from '../../api';
import { formatCurrency, SUBSCRIPTION_GRACE_DAYS, isInGracePeriod } from '../../subscription';

export default function TokenCountdown({ variant = 'appbar' }) {
    const { isAuthenticated, user, updateUser } = useAuth();
    const { hasToken, formatted, expiresAt } = useTokenCountdown();

    const [subTimeLeft, setSubTimeLeft] = React.useState('');
    const [subDays, setSubDays] = React.useState(null);
    const [inGrace, setInGrace] = React.useState(false);

    // Extend Dialog State
    const [extendDialogOpen, setExtendDialogOpen] = React.useState(false);
    const [planOptions, setPlanOptions] = React.useState([]);
    const [planOptionsLoading, setPlanOptionsLoading] = React.useState(false);
    const [planOptionsError, setPlanOptionsError] = React.useState('');
    const [extensionSubmitting, setExtensionSubmitting] = React.useState(false);
    const [selectedPlan, setSelectedPlan] = React.useState('');

    const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

    const handleOpenExtendDialog = async () => {
        setExtendDialogOpen(true);
        setPlanOptionsLoading(true);
        setPlanOptionsError('');
        try {
            const res = await getSubscriptionPlans();
            if (res.status >= 200 && res.status < 300) {
                const options = Array.isArray(res.data) ? res.data : [];
                setPlanOptions(options);
                // Standardmaessig aktuellen Plan vorselektieren oder den ersten verfügbaren
                const currentPlan = user?.paymentPlan;
                if (options.some(p => p.value === currentPlan)) {
                    setSelectedPlan(currentPlan);
                } else if (options.length > 0) {
                    setSelectedPlan(options[0].value);
                }
            } else {
                throw new Error('Tarife konnten nicht geladen werden.');
            }
        } catch (err) {
            setPlanOptionsError('Tarife konnten nicht geladen werden. Bitte versuche es erneut.');
        } finally {
            setPlanOptionsLoading(false);
        }
    };

    const handleCloseExtendDialog = () => {
        if (extensionSubmitting) return;
        setExtendDialogOpen(false);
    };

    const handleSubmitExtension = async () => {
        if (!selectedPlan) return;
        setExtensionSubmitting(true);
        try {
            const res = await changeSubscriptionPlan({ targetPlan: selectedPlan });
            if (res.status >= 200 && res.status < 300) {
                const checkoutUrl = res.data?.checkoutUrl;
                setSnackbar({
                    open: true,
                    message: 'Zahlung gestartet. Bitte schließe die Zahlung im neuen Fenster ab.',
                    severity: 'success'
                });
                setExtendDialogOpen(false);
                
                // User-Info refreshen (optional, Webhook macht die Hauptarbeit)
                const userRes = await getCurrentUser();
                if (userRes.data) {
                    updateUser(userRes.data);
                }

                if (checkoutUrl && typeof window !== 'undefined') {
                    window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
                }
            } else {
                const message = typeof res.data === 'string' && res.data.trim() 
                    ? res.data 
                    : 'Verlängerung konnte nicht gestartet werden.';
                setSnackbar({ open: true, message, severity: 'error' });
            }
        } catch (err) {
            const backendMessage = err?.response?.data;
            const message = typeof backendMessage === 'string' && backendMessage.trim()
                ? backendMessage
                : 'Fehler bei der Kommunikation mit dem Zahlungsanbieter.';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setExtensionSubmitting(false);
        }
    };

    React.useEffect(() => {
        if (!user?.paidUntil) {
            setSubTimeLeft('');
            setSubDays(null);
            setInGrace(false);
            return;
        }

        const calculateSubTime = () => {
            const now = new Date();
            const end = new Date(user.paidUntil);
            // paidUntil ist oft nur ein Datum (00:00:00), wir setzen es auf das Ende des Tages
            end.setHours(23, 59, 59, 999);
            const graceEnd = new Date(end);
            graceEnd.setDate(graceEnd.getDate() + SUBSCRIPTION_GRACE_DAYS);

            const diff = end - now;
            if (diff > 0) {
                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

                setSubDays(days);
                setInGrace(false);
                setSubTimeLeft(`${days} Tage ${hours} Stunden ${minutes} Minuten`);
                return;
            }

            // Karenzzeit: paidUntil überschritten, aber Zugang noch aktiv
            const graceDiff = graceEnd - now;
            if (graceDiff > 0) {
                const days = Math.floor(graceDiff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((graceDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((graceDiff % (1000 * 60 * 60)) / (1000 * 60));

                setSubDays(0);
                setInGrace(true);
                setSubTimeLeft(`Karenzzeit: noch ${days} Tage ${hours} Stunden ${minutes} Minuten`);
                return;
            }

            setSubTimeLeft('Abgelaufen');
            setSubDays(0);
            setInGrace(false);
        };

        calculateSubTime();
        const interval = setInterval(calculateSubTime, 60000); // Jede Minute aktualisieren
        return () => clearInterval(interval);
    }, [user?.paidUntil]);

    if (!isAuthenticated || !hasToken || !formatted) {
        return null;
    }

    const tooltipTitle = expiresAt
        ? `Abmeldung erfolgt am ${expiresAt.toLocaleString()}`
        : 'Zeit bis zur automatischen Abmeldung';

    const subTooltipTitle = user?.paidUntil
        ? (inGrace || isInGracePeriod(user)
            ? `Zahlung überfällig seit ${new Date(user.paidUntil).toLocaleDateString()} – Zugang noch ${SUBSCRIPTION_GRACE_DAYS} Tage in Karenzzeit aktiv. Bitte jetzt verlängern.`
            : `Abonnement läuft bis zum ${new Date(user.paidUntil).toLocaleDateString()}`)
        : '';

    // Farb-Logik fuer Abo
    const getSubColor = (theme, isBackground) => {
        if (subDays === null) return 'inherit';
        if (inGrace) {
            return isBackground ? alpha(theme.palette.warning.main, 0.15) : theme.palette.warning.dark;
        }
        if (subDays > 10) {
            return isBackground ? alpha(theme.palette.success.main, 0.1) : theme.palette.success.dark;
        } else if (subDays >= 5) {
            return isBackground ? alpha(theme.palette.warning.main, 0.1) : theme.palette.warning.dark;
        } else {
            return isBackground ? alpha(theme.palette.error.main, 0.1) : theme.palette.error.dark;
        }
    };

    const content = (
        <Stack direction="column" spacing={0.5} alignItems={variant === 'drawer' ? 'flex-start' : 'flex-end'}>
            <Tooltip title={tooltipTitle} enterTouchDelay={0} placement={variant === 'drawer' ? 'right' : 'bottom-start'}>
                <Box
                    sx={
                        variant === 'drawer'
                            ? (theme) => ({
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                                borderRadius: 1.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.main,
                                fontVariantNumeric: 'tabular-nums',
                                width: '100%',
                            })
                            : {
                                display: 'flex',
                                alignItems: 'center',
                                mr: 2,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1.5,
                                backgroundColor: 'rgba(0, 0, 0, 0.18)',
                                color: 'rgba(255, 255, 255, 0.92)',
                                fontVariantNumeric: 'tabular-nums',
                            }
                    }
                    data-testid="token-countdown"
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
                        Auto-Logout: {formatted}
                    </Typography>
                </Box>
            </Tooltip>

            {subTimeLeft && (
                <Tooltip title={subTooltipTitle} enterTouchDelay={0} placement={variant === 'drawer' ? 'right' : 'bottom-start'}>
                    <Box
                        sx={
                            variant === 'drawer'
                                ? (theme) => ({
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 1.5,
                                    backgroundColor: getSubColor(theme, true),
                                    color: getSubColor(theme, false),
                                    fontVariantNumeric: 'tabular-nums',
                                    width: '100%',
                                })
                                : {
                                    display: { xs: 'none', md: 'flex' },
                                    alignItems: 'center',
                                    mr: 2,
                                    px: 1.5,
                                    py: 0.2,
                                    borderRadius: 1.5,
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                    color: (subDays !== null && subDays < 5) ? '#ff4d4d' : (subDays !== null && subDays <= 10) ? '#ffa500' : '#4caf50',
                                    fontVariantNumeric: 'tabular-nums',
                                }
                        }
                    >
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'inherit' }}>
                            {inGrace ? 'Abo in Karenzzeit: ' : 'Abo läuft noch: '} <br /> {subTimeLeft}
                        </Typography>
                        {(inGrace || (subDays !== null && subDays < 10)) && (
                            <Button 
                                size="small" 
                                variant="text" 
                                onClick={handleOpenExtendDialog}
                                sx={{ 
                                    mt: 0.5, 
                                    p: 0, 
                                    fontSize: '0.75rem', 
                                    color: 'inherit', 
                                    textDecoration: 'underline',
                                    textTransform: 'none',
                                    minWidth: 0,
                                    '&:hover': { background: 'transparent', opacity: 0.8 }
                                }}
                            >
                                Abo verlängern
                            </Button>
                        )}
                    </Box>
                </Tooltip>
            )}

            <Dialog open={extendDialogOpen} onClose={handleCloseExtendDialog} fullWidth maxWidth="sm">
                <DialogTitle>Abonnement verlängern</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Wähle einen Tarif aus, um dein Abonnement zu verlängern oder zu ändern. 
                        Wir leiten dich anschließend zur sicheren Zahlung über Mollie weiter.
                    </DialogContentText>
                    
                    {planOptionsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : planOptionsError ? (
                        <Alert severity="error">{planOptionsError}</Alert>
                    ) : (
                        <FormControl component="fieldset" sx={{ width: '100%' }}>
                            <RadioGroup
                                value={selectedPlan}
                                onChange={(e) => setSelectedPlan(e.target.value)}
                            >
                                {planOptions.map((plan) => {
                                    const selected = selectedPlan === plan.value;
                                    const isCurrent = user?.paymentPlan === plan.value;
                                    return (
                                        <FormControlLabel
                                            key={plan.value}
                                            value={plan.value}
                                            control={<Radio />}
                                            sx={{
                                                m: 0,
                                                width: '100%',
                                                alignItems: 'flex-start',
                                                border: '1px solid',
                                                borderColor: selected ? 'primary.main' : 'divider',
                                                borderRadius: 2,
                                                px: 2,
                                                py: 1.5,
                                                mb: 1.5,
                                                backgroundColor: selected ? 'action.hover' : 'background.paper',
                                            }}
                                            label={
                                                <Stack spacing={0.5}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                            {plan.description}
                                                        </Typography>
                                                        {isCurrent && (
                                                            <Typography variant="caption" sx={{ bgcolor: 'primary.main', color: 'white', px: 1, borderRadius: 1 }}>
                                                                Aktuell
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {plan.tierLabel} – {plan.cycleLabel}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {formatCurrency(plan.amount)} {plan.yearly ? 'pro Jahr' : 'pro Monat'}
                                                    </Typography>
                                                </Stack>
                                            }
                                        />
                                    );
                                })}
                            </RadioGroup>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseExtendDialog} disabled={extensionSubmitting}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleSubmitExtension}
                        variant="contained"
                        disabled={extensionSubmitting || !selectedPlan}
                        sx={{ bgcolor: 'orange', color: '#000', '&:hover': { bgcolor: '#cc7a00' } }}
                    >
                        {extensionSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Jetzt verlängern'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Stack>
    );

    if (variant === 'drawer') {
        return (
            <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                {content}
                <Divider sx={{ mt: 1.5 }} />
            </Box>
        );
    }

    return content;
}

